import React, { useEffect, useRef, useState } from "react";
import { TextField, Button, Snackbar, Alert, AlertProps, Switch, FormControlLabel, FormControl, Grid, Box, InputAdornment,Checkbox } from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import { Option, SupplierViewModel } from "../../Types/ExportTypes";
import {
    CountryAutoComplete, CurrencyAutoComplete, Dimension1AutoComplete, Dimension2AutoComplete,
    PaymentTermsAutoComplete, VatbusPostingGroupAutoComplete, SalespersonAutoComplete, ShipmentMethodAutoComplete, SupplierAutoComplete, LanguageAutoComplete
} from "../../components/AutoComplete/AutoCompletes";
import InfoButton from "../../components/Tooltip/InfoTextField";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";
import ActionMenu, { ActionItem } from "../../components/Card/ActionMenu";
const EditSupplierPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { t } = useTranslation();
    const { supplierNo = "" } = params || {};
    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [supplier, setSupplier] = useState<SupplierViewModel | null>(null);

    const [shippingAgents, setShippingAgents] = useState<Option[]>([]);
    const [shippingServices, setShippingServices] = useState<Option[]>([]);

    const { goBack,navigate } = useNavigation();
    const [loadedOptions, setloadedOptions] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

    const hasPendingChanges = () => supplier?.hasChanges === true;

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    const preventIfUnsaved = () => {
        if (hasPendingChanges()) {
            showHasChangesError();
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (supplierNo) {
            setIsLoading(true);
            authService
                .get<SupplierViewModel>(`/supplierApi/GetSupplier?no=${supplierNo}`)
                .then((response) =>
                    setSupplier(
                        response
                            ? { ...response, originalValues: { ...response }, hasChanges: false }
                            : null
                    )
                )
                .catch((error: any) => {
                    showSnackbar(`${t("dialog.fetchSupplierError")} : ${error.response?.data ?? error.message}`, "error");
                })
                .finally(() => setIsLoading(false));
        } else {
            const defaultSupplier: SupplierViewModel = {
                name: "",
                name2: "",
                address: "",
                address2: "",
                city: "",
                phoneNo: "",
                blocked: false,
                postCode: "",
                eMail: "",
                contact: "",
                globalDimension1Code: "",
                globalDimension2Code: "",
                xMobileNo: "",
                county: "",
                newSupplier: true,
                originalValues: {},
                hasChanges: false,
            };
            defaultSupplier.originalValues = { ...defaultSupplier };
            setSupplier(defaultSupplier);
        }
        if (!loadedOptions) {
            initializeDropdowns();
            setloadedOptions(true);
        }
    }, [supplierNo, authService, setIsLoading]);


    const handleSave = async () => {
        if (!supplier) return;
        setIsLoading(true);
        try {
            const response = await authService.post<SupplierViewModel>("/supplierApi/CreateOrUpdateSupplier", supplier);
            setSupplier(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.savedSuccessfully"), "success");
        } catch {
            showSnackbar(t("dialog.saveErrorGeneric"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const initializeDropdowns = async () => {
        try {
            setIsLoading(true);


        } catch (error) {
            console.error("Failed to initialize dropdown options:", error);
        } finally {
            setIsLoading(false);
        }
    };



    const handleChange = (field: keyof SupplierViewModel, value: any) => {
        setSupplier((prev) => {
            if (!prev) return prev;
            return { ...prev, [field]: value, hasChanges: true };
        });
    };

    const handleCancelChanges = () => {
       // setIsLoading(true);
        try {
            setSupplier((prev) => {
                if (!prev || !prev.originalValues) return prev;
                const original = prev.originalValues;
                const changes: Partial<SupplierViewModel> = {};
                for (const key in original) {
                    const typedKey = key as keyof SupplierViewModel;
                    const originalValue = original[typedKey];
                    const currentValue = prev[typedKey];
                    if (originalValue !== currentValue) {
                        (changes as any)[typedKey] = originalValue;
                    }
                }
                 if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
                return { ...prev, ...changes, hasChanges: false };
            });
        } finally {
           // setIsLoading(false);
        }
    };


    const handleDelete = async () => {
        const isConfirmed = window.confirm(t("dialog.deleteItemConfirm"));
        if (!isConfirmed) return;
        if (!supplier) return;
        var newItem = supplier.newSupplier ?? false;
        if (newItem) {
            navigate({ path: "/purchase/supplier/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/supplierApi/DeleteSupplier`, supplier);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");

            navigate({ path: "/purchase/supplier/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.itemDeleteError")} : ${error.response?.data ?? error.message}`, "error");


        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = () => {
        const isConfirmed = window.confirm(t("dialog.areYouSure"));
        if (!isConfirmed) return;
        navigate({ path: "/purchase/supplier/edit", params: { supplierNo: "" } });
    };

    return (
        <>
            <CenteredCard
                ref={toolbarRef}
                title={t("common.edit") + " " + t("common.supplier")}
                isLoading={isLoading}
                handleSave={handleSave}
                handleCancel={handleCancelChanges}
                hasChanges={supplier?.hasChanges ?? false}
            >

                {supplier && (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "fit-content", mb: 2 }}>
                            <ActionMenu actions={[
                                { title: t("common.createNew"), onClick: handleCreate },
                                { title: t("common.delete"), onClick: handleDelete, color: "error" }

                            ]} />
                        </Box>
                        <Grid container spacing={2} >
                            <Grid item xs={12}>
                                {!supplier.newSupplier &&
                                    <TextField
                                    label={t("common.number")}
                                    value={supplier.no || ""}
                                    fullWidth
                                    margin="normal"
                                    disabled={ !supplier.newSupplier}
                                    />
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.name")}
                                    value={supplier.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    error={(!supplier.name || supplier.name.trim() === "")}
                                    helperText={
                                        !supplier.name?.trim() ? t("help.nameRequired") : ""
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierName")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.name") +" 2"}
                                    value={supplier.name2 || ""}
                                    onChange={(e) => handleChange("name2", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierNameAlt")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.address")}
                                    value={supplier.address || ""}
                                    onChange={(e) => handleChange("address", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierAddress")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.address") + " 2"}
                                    value={supplier.address2 || ""}
                                    onChange={(e) => handleChange("address2", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierAddressAlt")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.postCode")}
                                    value={supplier.postCode || ""}
                                    onChange={(e) => handleChange("postCode", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 50 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.city")}
                                    value={supplier.city || ""}
                                    onChange={(e) => handleChange("city", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CountryAutoComplete
                                        key={supplier.countryCode || null}
                                        valueId={supplier.countryCode || null}
                                        onChange={(selected) => handleChange("countryCode", selected?.value || "")}

                                    />
                                </FormControl>
                            </Grid>

                       

                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.phoneNo")}
                                    value={supplier.phoneNo || ""}
                                    onChange={(e) => handleChange("phoneNo", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierPhone")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.email")}
                                    value={supplier.eMail || ""}
                                    onChange={(e) => handleChange("eMail", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 80 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierEmail")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.contact")}
                                    value={supplier.contact || ""}
                                    onChange={(e) => handleChange("contact", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.supplierContact")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            {/*    <Grid item xs={12}>*/}
                            {/*<TextField*/}
                            {/*    label="Global Dimension 1 Code"*/}
                            {/*    value={customer.globalDimension1Code || ""}*/}
                            {/*    onChange={(e) => handleChange("globalDimension1Code", e.target.value)}*/}
                            {/*    fullWidth*/}
                            {/*    margin="normal"*/}
                            {/*        />*/}
                            {/*    </Grid>*/}
                            {/*    <Grid item xs={12}>*/}
                            {/*<TextField*/}
                            {/*    label="Global Dimension 2 Code"*/}
                            {/*    value={customer.globalDimension2Code || ""}*/}
                            {/*    onChange={(e) => handleChange("globalDimension2Code", e.target.value)}*/}
                            {/*    fullWidth*/}
                            {/*    margin="normal"*/}
                            {/*        />*/}
                            {/*</Grid>*/}

              

                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CurrencyAutoComplete
                                        key={supplier.currencyCode || null}
                                        valueId={supplier.currencyCode || null}
                                        onChange={(selected) => handleChange("currencyCode", selected?.value || "")}
                                        infoText={t("help.supplierCurrency")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <LanguageAutoComplete
                                        key={supplier.languageCode || null}
                                        valueId={supplier.languageCode || null}
                                        onChange={(selected) => handleChange("languageCode", selected?.value || "")}
                                        infoText={t("help.supplierLanguage")}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} lg={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!supplier.blocked} onChange={(e) => handleChange("blocked", e.target.checked)} />}
                                    label={t("common.blocked")}
                                />
                            </Grid>
         
                            {/* Toggle for Settings Section */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                                    label={t("common.showAdvancedSettings")}
                                />
                            </Grid>

                            {/* Settings Section */}
                            {showSettings && (
                                <>
                                    <Box
                                        sx={{
                                            backgroundColor: "secondary.main",
                                            padding: 2,
                                            borderRadius: 2,
                                            width: "100%"
                                        }}
                                    >

                              
                             
                                  
                                   
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <PaymentTermsAutoComplete
                                                    key={supplier.paymentTermsCode || null}
                                                    valueId={supplier.paymentTermsCode || null}
                                                    onChange={(selected) => handleChange("paymentTermsCode", selected?.value || "")}
                                                />


                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <ShipmentMethodAutoComplete
                                                    key={supplier.shipmentMethodCode || null}
                                                    valueId={supplier.shipmentMethodCode || null}
                                                    onChange={(selected) => handleChange("shipmentMethodCode", selected?.value || "")}
                                                />

                                            </FormControl>
                                        </Grid>
                        
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <Dimension1AutoComplete
                                                    key={supplier.globalDimension1Code || null}
                                                    valueId={supplier.globalDimension1Code || null}
                                                    onChange={(selected) => handleChange("globalDimension1Code", selected?.value || "")}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <Dimension2AutoComplete
                                                    key={supplier.globalDimension2Code || null}
                                                    valueId={supplier.globalDimension2Code || null}
                                                    onChange={(selected) => handleChange("globalDimension2Code", selected?.value || "")}
                                                />
                                            </FormControl>
                                        </Grid>

                                    </Box>
                                </>
                            )}
                        </Grid>
                    </>
                )}


          <Box sx={{ marginTop: 2, textAlign: "center" }}>

            <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
              {t("common.save")}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          </Box>
            </CenteredCard>


        </>
    );
};

export default EditSupplierPage;
